import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { AuthService } from '../services/auth-service';
const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;
import { cachedFetch } from '../utils/cachedFetch.js';

@customElement('app-statistics')
export class AppStatistics extends LitElement {
  @state() private stats: { label: string; value: string | number }[] = [];
  @state() private workersStats: { workerNumber: string; Name: string; totalBHTHours: number; totalEXTHours: number }[] = [];
  @state() private errorMessage: string = '';
  @state() private showBhtHours = true;

  static styles = css`
    :host {
      display: block;
      font-family: Arial, sans-serif;
      padding: 16px;
      background-color: var(--background-color, #ffffff);
      color: var(--text-color, #000000);
      transition: background-color 0.3s ease, color 0.3s ease;
    }

    h1 {
      font-size: 1.5rem;
      margin-bottom: 16px;
    }

    .stats-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 16px;
      max-height: 80vh;
      overflow-y: auto;
      padding-bottom: 16px;
    }

    .stat-card {
      background: var(--card-bg, #f9f9f9);
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      text-align: center;
    }

    .stat-card h3 {
      font-size: 1.2rem;
      margin-bottom: 8px;
    }

    .stat-card p {
      font-size: 1rem;
      color: var(--text-secondary, #555);
    }

    .error-message {
      color: red;
      margin-top: 16px;
      text-align: center;
    }

    .workers-card {
      grid-column: span 2;
      width: 100%;
      text-align: left;
      max-width: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }

    .workers-list {
      max-height: 200px;
      overflow-y: auto;
      padding-right: 8px;
    }

    .worker-item {
      padding: 12px;
      border-radius: 8px;
      margin-bottom: 8px;
      background-color: var(--card-bg, #f9f9f9);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .highlight {
      background-color: #003366;
      color: white;
    }

    .note {
      font-size: 0.7rem !important; /* Force smaller text */
      color: var(--text-secondary, #777); /* Lighter color */
      margin-bottom: 6px;
      line-height: 1.2; /* Improve readability */
      text-align: left;
      max-width: 100%;
      white-space: normal; /* Ensure text wraps properly */
    }

    button {
      background-color: var(--card-bg, #f9f9f9);
      border: 1px solid #ccc;
      padding: 8px 16px;
      margin: 0 4px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 1rem;
      transition: background-color 0.3s ease, color 0.3s ease;
    }

    button[disabled] {
      background-color: #003366;
      color: white;
      cursor: default;
      border-color: #002244;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.setMetaTheme();
    this.fetchStatistics();
  }

  private setMetaTheme() {
    const lightThemeColor = '#ffffff';
    const darkThemeColor = '#181818';

    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.remove());

    document.head.insertAdjacentHTML(
      'beforeend',
      `
      <meta name="theme-color" media="(prefers-color-scheme: light)" content="${lightThemeColor}">
      <meta name="theme-color" media="(prefers-color-scheme: dark)" content="${darkThemeColor}">
    `
    );

    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (prefersDarkScheme) {
      this.style.setProperty('--background-color', '#181818');
      this.style.setProperty('--text-color', '#ffffff');
      this.style.setProperty('--card-bg', '#242424');
      this.style.setProperty('--text-secondary', '#aaaaaa');
    } else {
      this.style.setProperty('--background-color', '#ffffff');
      this.style.setProperty('--text-color', '#000000');
      this.style.setProperty('--card-bg', '#f9f9f9');
      this.style.setProperty('--text-secondary', '#555555');
    }
  }

  async fetchStatistics() {
    try {
      const year = new Date().getFullYear();
      const workerNumber = AuthService.workerNumber;

      if (!workerNumber) {
        this.errorMessage = 'No worker is logged in.';
        return;
      }

      // Fetch BHT hours for the current worker
      const bhtResponse = await cachedFetch(`${apiServerUrl}/bht-hours/${workerNumber}?year=${year}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
      });
      if (!bhtResponse.ok) throw new Error('Failed to fetch BHT hours.');

      const bhtData = await bhtResponse.json();
      const bhtHoursStat = {
        label: 'Total BHT Hours',
        value: bhtData.totalBHTHours,
      };

      // Fetch anticipated night shifts for the current worker
      const nightsResponse = await cachedFetch(`${apiServerUrl}/anticipated-night-shift/${workerNumber}?year=${year}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
      });
      if (!nightsResponse.ok) throw new Error('Failed to fetch anticipated night shifts.');

      const nightsData = await nightsResponse.json();
      const anticipatedNightsStat = {
        label: 'Flexible Night Shifts',
        value: nightsData.numberOfNights,
      };

      // Fetch BHT hours for all workers
      const allBhtResponse = await cachedFetch(`${apiServerUrl}/all-worker-hours?year=${year}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
      });
      if (!allBhtResponse.ok) throw new Error('Failed to fetch all worker BHT hours.');

      const allBhtData = await allBhtResponse.json();

      // Save all worker statistics
      this.workersStats = allBhtData;

      // Combine the statistics
      this.stats = [bhtHoursStat, anticipatedNightsStat];
    } catch (error) {
      console.error('Error fetching statistics:', error);
      this.errorMessage = 'Error fetching statistics. Please try again later.';
    }
  }

  render() {
    return html`
      <app-header></app-header>
      <h1>Statistics</h1>

      ${this.errorMessage
        ? html`<p class="error-message">${this.errorMessage}</p>`
        : html`
            <div class="stats-container">
              <div class="stat-card">
                <h3>${this.stats[0]?.label}</h3>
                <p>${this.stats[0]?.value}</p>
              </div>
              <div class="stat-card">
                <h3>${this.stats[1]?.label}</h3>
                <p class="note">Anticipated S/D (Up to 4 per year).</p>
                <p>${this.stats[1]?.value}</p>
              </div>

              <!-- Toggle Buttons -->
              <div style="grid-column: span 2; text-align: center; margin-bottom: 16px;">
                <button
                  @click=${() => (this.showBhtHours = true)}
                  ?disabled=${this.showBhtHours}
                >
                  BHT Hours
                </button>
                <button
                  @click=${() => (this.showBhtHours = false)}
                  ?disabled=${!this.showBhtHours}
                >
                  Extra Hours
                </button>
              </div>

              ${this.showBhtHours
                ? html`
                    <div class="stat-card workers-card" style="grid-column: span 2;">
                      <h3>BHT Hours</h3>
                      <p class="note">
                        This data follows the color coding from the PDF file.
                        Possible shift swaps between workers may not be reflected.
                      </p>
                      <div class="workers-list">
                        ${this.workersStats?.map(
                          (worker) => html`
                            <div
                              class="worker-item ${worker.workerNumber === AuthService.workerNumber ? 'highlight' : ''}"
                            >
                              <span>${worker.Name}</span>
                              <span>${worker.totalBHTHours}</span>
                            </div>
                          `
                        )}
                      </div>
                    </div>
                  `
                : html`
                    <div class="stat-card workers-card" style="grid-column: span 2;">
                      <h3>Extra Hours</h3>
                      <p class="note">
                        This data follows the color coding from the PDF file.
                        Possible shift swaps between workers may not be reflected.
                      </p>
                      <div class="workers-list">
                       ${this.workersStats
  ?.filter((worker) => worker.totalEXTHours > 0)
  .sort((a, b) => b.totalEXTHours - a.totalEXTHours)  // <-- Sort descending here
  .map(
    (worker) => html`
      <div
        class="worker-item ${worker.workerNumber === AuthService.workerNumber ? 'highlight' : ''}"
      >
        <span>${worker.Name}</span>
        <span>${worker.totalEXTHours}</span>
      </div>
    `
  )}

                      </div>
                    </div>
                  `}
            </div>
          `}
    `;
  }
}
